import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import { Button, Input } from "@mui/material";
import { useKeycloak } from "@react-keycloak/web";
import { useQueryClient } from "@tanstack/react-query";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { selectAtom } from "jotai/utils";
import { noop } from "lodash";
import { FC, useEffect, useRef, useState } from "react";
import {
  baseAtom,
  formatStatus,
  removeInterviewByID,
  setUppyFiles,
} from "../../../stores/jotai/base";
import { bulkRecordingPopupStateAtom } from "../../../stores/jotai/bulkRecordingPopupState";
import { discoverySessionAtom } from "../../../stores/jotai/discovery-session";
import {
  fileUploadAtom,
  setUppyUploadRequestCount,
} from "../../../stores/jotai/fileUpload";
import { interviewsAtom } from "../../../stores/jotai/interviews/interviews";
import { hasPermissionCanvasWrite } from "../../../stores/jotai/permissions";
import { useCreateInterview } from "../../../tanstackQuery/queries/interviews";
import { cancelRecordingUpload } from "../../../util/cancelRecordingUpload";
import MaterialModal from "../../app/material-modal";
import BulkRecordingUploadDialog from "./BulkRecordingUploadDialog";
import MaterialInterviewDetails from "./material-interview-details";
import { addFileToUppy, createUppyInstance, destroyUppyInstance } from "../utils/uppy";

interface InterviewRecordingUploaderProps {
  interviewID?: string;
  renderButton?: Function;
  renderVideoButton?: Function;
  onUppyInitialization?: Function;
  onInterviewModalClose?: Function;
  disabled?: boolean;
  primaryInstance?: boolean;
  hideImportButton?: boolean;
}

interface NewInterviewToEditState {
  interviewID: string;
  interviewee: string;
  templateID?: string,
}
const userAtom = selectAtom(baseAtom, (base) => base.user);
const teamAtom = selectAtom(baseAtom, (base) => base.team);
const orgAtom = selectAtom(baseAtom, (base) => base.org);
const getFileNameWithoutExtension = (
  fileNameWithExtension: string,
  extension: string
) => fileNameWithExtension.split(`.${extension}`)[0];

const InterviewRecordingUploader: FC<InterviewRecordingUploaderProps> = (
  props
) => {
  const {
    interviewID,
    renderButton,
    renderVideoButton,
    onUppyInitialization,
    onInterviewModalClose,
    disabled,
  } = props;
  const discoverySession = useAtomValue(discoverySessionAtom);
  const [base, setBase] = useAtom(baseAtom);
  const setFileUploadAtomState = useSetAtom(fileUploadAtom);
  const [fileIdToUpload, setFileIdToUpload] = useState<string>(null);
  const { willShow: shouldShowUploadDialog } = useAtomValue(
    bulkRecordingPopupStateAtom
  );
  const [allInterview] = useAtom(interviewsAtom);
  const teamID = base?.team?.groupID;
  const [showBulkRecordingUploadDialog, setShowBulkRecordingUploadDialog] =
    useState<boolean>(false);
  const [selectedRecordingFiles, setSelectedRecordingFiles] =
    useState<FileList | null>(null);
  const [newInterviewToEdit, setnewInterviewToEdit] =
    useState<NewInterviewToEditState | null>(null);
  const { keycloak } = useKeycloak();

  const canEditCanvas = hasPermissionCanvasWrite(base);

  const client = useQueryClient();
  const { mutateAsync: handleCreateInterview } = useCreateInterview(
    { accessToken: keycloak?.token },
    {
      onSuccess() {
        client.invalidateQueries({ queryKey: ["interviews"] });
      },
    }
  );

  const bulkImportEnabled = !interviewID;
  const createNewInterview = bulkImportEnabled;
  const recordingUploadInputRef = useRef(null);
  const [team] = useAtom(teamAtom);
  const [user] = useAtom(userAtom);
  const [org] = useAtom(orgAtom);
  const onUploadButtonClick = () => {
    openFileSelectorModal();
  };

  const onUploadVideoButtonClick = () => {
    openVIDEOFileSelectorModal();
  };

  const openFileSelectorModal = () => {
    recordingUploadInputRef?.current?.click();
  };

  const openVIDEOFileSelectorModal = () => {
    recordingUploadInputRef?.current?.click();
  };

  const importButtonProps = {
    startIcon: <RecordVoiceOverIcon />,
    variant: "outlined",
    children: "Import",
    disabled: disabled || !canEditCanvas,
    onClick: onUploadButtonClick,
  };

  const updateInterviewDetails = async ({
    interviewID,
    interviewee,
    uppyDiscoverySession,
  }) => {
    const interviewDetails = {
      interviewID,
      interviewee,
      meta_City: "",
      meta_State: "",
      "meta_Company Size": "",
    };

    await handleCreateInterview({
      ...interviewDetails,
      speakers: undefined,
      status: formatStatus(
        interviewDetails ? interviewDetails.status : "draft"
      ),
      groupID: base.team.groupID,
      sessionID: discoverySession.sessionID,
    });
  };

  const handleEditDataOfInterview = (interview: NewInterviewToEditState) => {
    const { discoverySession: uppyDiscoverySession } = uppy.opts.meta;

    updateInterviewDetails({
      interviewID: interview?.interviewID,
      interviewee: interview?.interviewee,
      uppyDiscoverySession,
    });

    setnewInterviewToEdit({
      ...interview,
      templateID: newInterviewToEdit?.templateID || null,
    });
  };

  const onGetStorageInfo = (file, response) => {
    const { originalName, extension, meta } = file;
    const { canEditInterviewDetails, interviewID } = meta;
    const originalFileNameWithoutExtension = getFileNameWithoutExtension(
      originalName,
      extension
    );

    if (canEditInterviewDetails) {
      handleEditDataOfInterview({
        interviewID,
        interviewee: originalFileNameWithoutExtension,
      });
    }
  };

  const [uppy] = useState(() =>
    createUppyInstance(discoverySession, teamID, onGetStorageInfo)
  );

  const handleBulkRecordingUploadDialogProceed = (event) => {
    onInterviewModalClose();
    setShowBulkRecordingUploadDialog(false);
    uploadRecordings();
  };

  const handleBulkRecordingUploadDialogDiscard = (event) => {
    onInterviewModalClose();
    setShowBulkRecordingUploadDialog(false);
    clearRecordingFileInput();
  };

  const handleBulkRecordingUpload = () => {
    const fileCount = selectedRecordingFiles?.length || 0;

    if (fileCount > 1 && shouldShowUploadDialog) {
      setShowBulkRecordingUploadDialog(true);
    } else {
      uploadRecordings();
    }
  };

  const uploadRecordings = async () => {
    if (selectedRecordingFiles) {
      for (let i = 0; i < selectedRecordingFiles?.length; i++) {
        try {
          const file = selectedRecordingFiles[i];

          await addFileToUppy({
                file,
                uppy,
                interviewID,
                canEditInterviewDetails: bulkImportEnabled && selectedRecordingFiles?.length === 1,
                setFileIdToUpload,
          });
        
          if (!createNewInterview) {
            // if the user is uploading file to an existing interview
            // it will upload only single file i.e. the first file here
            break;
          }
        } catch (error) {
          console.error(error);
        }
      }

      setUppyFiles(setBase, uppy.getFiles(fileIdToUpload));
      setUppyUploadRequestCount(
        setFileUploadAtomState,
        selectedRecordingFiles?.length
      );
      clearRecordingFileInput();
    }
  };

  const onRecordingFileSelection = (event) => {
    const selectedFiles: FileList = event?.target?.files;

    setSelectedRecordingFiles(selectedFiles);
  };

  const clearRecordingFileInput = () => {
    setSelectedRecordingFiles(null);

    if (recordingUploadInputRef?.current) {
        recordingUploadInputRef.current.value = null;
    }

    if (recordingUploadInputRef?.current) {
        recordingUploadInputRef.current.value = null;
    }
  };

  const VIDEO_importButtonProps = {
    startIcon: <RecordVoiceOverIcon />,
    variant: "outlined",
    children: "Import",
    onClick: onUploadVideoButtonClick,
  };

  const processRecordingOnSuccessfullUpload = (result) => {
    // @ts-ignore
    const { discoverySession: uppyDiscoverySession } = uppy.opts.meta;
    const { originalName, extension, meta } = result;
    const {
      interviewID: recordingInterviewID,
      canEditInterviewDetails,
      shouldCreateOrUpdateInterview,
      artifactID,
      dataURL,
      vultrKey,
      transcriptArtifactID,
    } = meta;
    const originalNameWithoutExtension = originalName.split(`.${extension}`)[0];

    client.invalidateQueries({ queryKey: ["artifacts"] });

    // canEditInterviewDetails meta option will determine that if it was
    // a multi files upload or single file upload
    // in case of multi upload, we'll rename the interviewee name with the file name
    // in case of single upload we'll show interview detail edit modal
    // which is implemented inside onGetStorageInfo functionn
    if (
      shouldCreateOrUpdateInterview &&
      createNewInterview &&
      !canEditInterviewDetails
    ) {
      updateInterviewDetails({
        interviewID: recordingInterviewID,
        // replacing the interviewee name with the filename
        interviewee: originalNameWithoutExtension,
        uppyDiscoverySession,
      });
    }
  };

  const postSuccessfullUploadTasks = () => {
    setUppyFiles(setBase, uppy.getFiles(fileIdToUpload));
    clearRecordingFileInput();
  };

  const onInterviewDetailModalClose = () => {
    setnewInterviewToEdit(null);
    if (onInterviewModalClose) {
      onInterviewModalClose();
    }
  };

  const onUploadProgress = (file, progress) => {
    setUppyFiles(setBase, uppy.getFiles(fileIdToUpload));
  };

  const onFileRemove = () => {
    clearRecordingFileInput();

    setUppyFiles(setBase, uppy.getFiles());
  };

  const onUploadSuccess = (file) => processRecordingOnSuccessfullUpload(file);

  const onComplete = () => {
    postSuccessfullUploadTasks();
    client.invalidateQueries({ queryKey: ["interviews"]});
  };

  const cancelAllUploads = () => {
    uppy.getFiles(fileIdToUpload).forEach((file) => {
      cancelRecordingUpload({
        file,
        uppy,
        discoverySession,
        teamID,
        onSuccess: () => {
          removeInterviewByID(setBase, file.meta.interviewID, allInterview);
        },
      });
    });
  };

  useEffect(() => {
    uppy.on("upload-progress", onUploadProgress);
    uppy.on("file-removed", onFileRemove);
    uppy.on("upload-success", onUploadSuccess);
    uppy.on("complete", onComplete);

    onUppyInitialization(uppy);

    return () => {
      uppy.off("upload-progress", onUploadProgress);
      uppy.off("file-removed", onFileRemove);
      uppy.off("upload-success", onUploadSuccess);
      uppy.off("complete", onComplete);

      cancelAllUploads();
      destroyUppyInstance(uppy);
    };
  }, []);

  useEffect(() => {
    uppy.setMeta({
      discoverySession,
      teamID,
    });
  }, [discoverySession, teamID]);

  useEffect(() => {
    if (bulkImportEnabled) {
      handleBulkRecordingUpload();
    } else {
      uploadRecordings();
    }
  }, [selectedRecordingFiles]);

  let standardImportButton = renderButton(importButtonProps);

  if (props.hideImportButton === true) {
    standardImportButton = undefined;
  }

  return (
    <>
      <Input
        inputRef={recordingUploadInputRef}
        id="recording-upload"
        type="file"
        inputProps={{
          // if an interviewID provided, it'll upload the file to that interview.
          multiple: bulkImportEnabled,
          accept: "audio/*,video/*",
        }}
        sx={{
          display: "none",
        }}
        onChange={onRecordingFileSelection}
      />
      {standardImportButton}
      {renderVideoButton
        ? renderVideoButton(VIDEO_importButtonProps)
        : undefined}
      <BulkRecordingUploadDialog
        open={showBulkRecordingUploadDialog}
        onProceed={handleBulkRecordingUploadDialogProceed}
        onDiscard={handleBulkRecordingUploadDialogDiscard}
      />
      <MaterialModal
        title="Interview Details"
        open={!!newInterviewToEdit}
        handleDialogClose={onInterviewDetailModalClose}
        styleOverrides={{
          container: {
            "& .MuiDialog-paper": {
              width: "100%",
              minHeight: "500px",
              minWidth: "780px",
            },
          },
        }}
      >
        <MaterialInterviewDetails
          showSaveAndOpenButton={true}
          style={{ width: "812px" }}
          interview={{
            interviewID: newInterviewToEdit?.interviewID,
            interviewee: newInterviewToEdit?.interviewee,
            templateID: newInterviewToEdit?.templateID || null,
          }}
          existingInterview={true}
          save={noop}
          user={user}
          team={team}
          org={org}
          errors={[]}
          close={onInterviewDetailModalClose}
          handleErrors={(e) => {
            alert(e);
            return true;
          }}
          setBase={setBase}
        />
      </MaterialModal>
    </>
  );
};

InterviewRecordingUploader.defaultProps = {
  interviewID: null,
  renderButton: (buttonProps = {}) => <Button {...buttonProps} />,
  onUppyInitialization: noop,
  disabled: false,
};

export default InterviewRecordingUploader;
